/**
 * Authenticated desktop shell — header, sidebar, page content, floating help.
 *
 * Help / tour are **not** mounted eagerly. `LazyHelpModal` loads instruction JSON and
 * the help JS chunk only when the user clicks the FloatButton, keeping those namespaces
 * out of the first-paint i18n gate (`SHELL_NAMESPACES`).
 *
 * @see components/universal/InstructionsModal/LazyHelpModal.jsx
 * @see docs/translate/preload.md
 */
import { useState, useEffect } from "react";
import { Layout, Spin, FloatButton } from "antd";
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { logout as authLogout } from "auth";
import LazyHelpModal from "components/universal/InstructionsModal/LazyHelpModal";
import useAuthStore from "stores/useAuthStore";
import Header from "../../Header/Header";
import Sidebar from "../../Sidebar/Sidebar";
import "../../../css/index.css";

const { Content } = Layout;

const AppLayout = ({ children }) => {
  const userData = useAuthStore((s) => s.userData);
  const pageTitle = useAuthStore((s) => s.pageTitle);
  const documentTitle = useAuthStore((s) => s.documentTitle);

  const navigate = useNavigate();
  const { t } = useTranslation(["common","terms"]);

  const [onUpdate, setOnUpdate] = useState(false);

  const location = useLocation();
  const currentPath = location.pathname + location.search;

  const [instructionsModalVisible, setInstructionsModalVisible] = useState(false);
  const hasTour = (() => {
    const tourPages = ["/dashboard", "/frameworks", "/obligations", "/files"];
    return tourPages.some(p => currentPath.startsWith(p)) || (currentPath.startsWith("/calendar") && currentPath.includes("id="));
  })();

  const logout = () => {
    localStorage.removeItem("policies:lastRun");
    localStorage.removeItem("policy_snooze_until");
    const { redirectPath } = authLogout();
    navigate(redirectPath);
  };

  // Update browser tab title
  useEffect(() => {
    document.title = documentTitle;
  }, [documentTitle]);

  // Reset update flag
  useEffect(() => {
    if (onUpdate) setOnUpdate(false);
  }, [onUpdate]);

  if (!userData) return <p>Loading user data...</p>;

  return (
    <Layout style={{ height: "100vh" }}>
      <Header onLogout={logout} type="dashboard" />

      <Layout style={{ minHeight: 0, flex: 1, background: "#f5f5ff", overflow: "auto" }}>
        <Sidebar />

        <Content
          style={{
            minHeight: 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            background: "white",
            overflow: "auto",
          }}
        >
          <div>{pageTitle}</div>

          <div
            className="module-container"
            style={{
              height: "inherit",
              minHeight: 0,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            {children}
          </div>

          <FloatButton
            icon={<QuestionCircleOutlined />}
            type="primary"
            style={{ right: 24, bottom: 24, zIndex: 1205 }}
            tooltip={t("common:help")}
            onClick={() => setInstructionsModalVisible(true)}
          />

          <LazyHelpModal
            visible={instructionsModalVisible}
            userData={userData || null}
            onClose={() => setInstructionsModalVisible(false)}
            currentPath={currentPath}
            hasTour={hasTour}
          />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
